
import { defineComponent, onBeforeMount, reactive } from 'vue'

// UI
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// Module Components
import MerchantsActions from '@/modules/MerchantsModule/components/MerchantsActions/index.vue'

// Classes
import { DataState } from '@/core/classes/DataState.class'

// Types
import { CreateMerchantPayloadT, MerchantT } from '@/core/types/merchants.types'

// Realisations
import { merchants } from '@/core/realisations/merchants.realisation'

// Composable
import useActions from '@/common/composable/useActions'
import useNotifications from '@/common/composable/useNotifications'

// constants
import { MerchantsColumns } from '@/core/constants/columns/Merchants.columns'
import { CompanyT } from '@/core/types/companies.types'
import { companies } from '@/core/realisations/companies.realisation'

const ACTIVE_OPTIONS = [
    { id: 1, name: 'Активный', value: '1' },
    { id: 2, name: 'Не активный', value: '0' },
]

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'failed-fetch': FailedFetch,
        'the-button': TheButton,
        //
        'merchants-actions': MerchantsActions,
        TableScrollWrapper,
        TheTable,
        TheSelect,
    },
    setup() {
        const state = reactive(new DataState<MerchantT>({}))
        const filters = reactive({
            companyId: null as number | null,
            isActive: null as number | null,
        })

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
        })

        const loadMerchants = async (page: number) => {
            try {
                state.is_loading = true
                const params = {
                    page,
                    //
                    companyId: filters.companyId,
                    isActive: filters.isActive ? Number(filters.isActive) : null,
                }

                const response = await merchants.getMerchantsList(params)
                state.list = response.data
                state.pagination.total_pages = response.meta.last_page

                state.is_failed = false
                state.failure.error_state.is_cancel = false
            } catch (error) {
                state.failure.catchError(error)

                if (!state.failure.error_state.is_cancel) {
                    state.is_failed = true
                }
            } finally {
                if (!state.failure.error_state.is_cancel) {
                    state.is_loading = false
                }
            }
        }

        const { actions_state, startCreate, startUpdate, startDelete } = useActions<MerchantT>()
        const notifications = useNotifications()

        const onMerchantCreate = async (payload: CreateMerchantPayloadT) => {
            try {
                actions_state.is_actions_loading = true

                await merchants.createMerchant(payload)
                notifications.notify({ title: 'Мерчант создан', type: 'success' })

                await loadMerchants(state.pagination.current_page)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'Ошибка при создании мерчанта' })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const onMerchantUpdate = async ({
            id,
            payload,
        }: {
            id: number
            payload: CreateMerchantPayloadT
        }) => {
            try {
                actions_state.is_actions_loading = true

                await merchants.updateMerchant(id, payload)
                notifications.notify({ title: 'Мерчант сохранен', type: 'success' })

                await loadMerchants(state.pagination.current_page)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'Ошибка при редактировании мерчанта' })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const onMerchantDelete = async (merchant_id: number) => {
            try {
                actions_state.is_actions_loading = true

                await merchants.deleteMerchant(merchant_id)
                notifications.notify({ title: 'Мерчант удалён', type: 'success' })

                await loadMerchants(state.pagination.current_page)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'Ошибка при удалении мерчанта' })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page,
                    search,
                    statusId: 2,
                    isExtended: 0,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, statusId: 2, isExtended: 0 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const resetFilters = () => {
            if (state.is_loading) return

            filters.companyId = null
            filters.isActive = null

            loadMerchants(1)
        }

        onBeforeMount(() => {
            loadMerchants(1)
            loadCompanies(1)
        })

        return {
            state,
            filters,
            data_state,
            loadMerchants,
            onMerchantCreate,
            onMerchantUpdate,
            onMerchantDelete,
            actions_state,
            startCreate,
            startUpdate,
            startDelete,
            loadCompanies,
            loadMoreCompanies,
            resetFilters,
            //
            MerchantsColumns,
            ACTIVE_OPTIONS,
        }
    },
})
